import { FieldType } from '../Field';
import { BaseVisitor } from './Parser/Visitor';
export class Compiler extends BaseVisitor {
    constructor(fieldResolver, funcResolver, typeResolver) {
        super();
        this._fieldResolver = fieldResolver;
        this._funcResolver = funcResolver;
        this._typeResolver = typeResolver;
        this._init = new Array();
        this._step = new Array();
        this._final = new Array();
        this._functions = new Array();
        this._dataFields = new Array();
    }
    get fieldResolver() {
        return this._fieldResolver;
    }
    get funcResolver() {
        return this._funcResolver;
    }
    get typeResolver() {
        return this._typeResolver;
    }
    get init() {
        return this._init;
    }
    get step() {
        return this._step;
    }
    get final() {
        return this._final;
    }
    get functions() {
        return this._functions;
    }
    get returnType() {
        return this.typeResolver.returnType;
    }
    get dataFields() {
        return this._dataFields;
    }
    get isScalar() {
        return !this.isAggregate;
    }
    get isAggregate() {
        return (this.functions.filter((func) => {
            return func.type === 'AGG';
        }).length > 0);
    }
    /**
     * Visitors
     */
    visitIdentifierNode(node) {
        this.final.push(() => node.name);
    }
    visitNullLiteralNode(node) {
        this.final.push(() => null);
    }
    visitBooleanLiteralNode(node) {
        this.final.push(() => node.value);
    }
    visitNumberLiteralNode(node) {
        this.final.push(() => node.value);
    }
    visitHexLiteralNode(node) {
        this.final.push(() => node.value);
    }
    visitStringLiteralNode(node) {
        this.final.push(() => node.value);
    }
    visitRegExpLiteralNode(node) {
        this.final.push(() => node.value);
    }
    visitDateLiteralNode(node) {
        this.final.push(() => node.value);
    }
    visitFieldLiteralNode(node) {
        this.pushField(node);
    }
    visitUnaryExpressionNode(node) {
        this.pushUnary(node);
    }
    visitBinaryExpressionNode(node) {
        this.pushBinary(node);
    }
    visitLogicalExpressionNode(node) {
        this.pushBinary(node);
    }
    visitCaseExpressionNode(node) {
        this.pushCase(node);
    }
    visitCallExpressionNode(node) {
        this.pushCall(node);
    }
    pushUnary(node) {
        const operand = this.final.pop();
        switch (node.operator) {
            case 'NOT':
                this.final.push((scope) => !operand(scope));
                break;
            case '-':
                this.final.push((scope) => -operand(scope));
                break;
            case '+':
                this.final.push((scope) => +operand(scope));
                break;
        }
    }
    pushBinary(node) {
        const operand2 = this.final.pop();
        const operand1 = this.final.pop();
        switch (node.operator) {
            case 'OR':
                this.final.push((scope) => operand1(scope) || operand2(scope));
                break;
            case 'AND':
                this.final.push((scope) => operand1(scope) && operand2(scope));
                break;
            case '=':
                this.final.push((scope) => operand1(scope) === operand2(scope));
                break;
            case '<>':
                this.final.push((scope) => operand1(scope) !== operand2(scope));
                break;
            case '>':
                this.final.push((scope) => operand1(scope) > operand2(scope));
                break;
            case '<':
                this.final.push((scope) => operand1(scope) < operand2(scope));
                break;
            case '>=':
                this.final.push((scope) => operand1(scope) >= operand2(scope));
                break;
            case '<=':
                this.final.push((scope) => operand1(scope) <= operand2(scope));
                break;
            case '+':
                this.final.push((scope) => operand1(scope) + operand2(scope));
                break;
            case '-':
                this.final.push((scope) => operand1(scope) - operand2(scope));
                break;
            case '*':
                this.final.push((scope) => operand1(scope) * operand2(scope));
                break;
            case '/':
                this.final.push((scope) => operand1(scope) / operand2(scope));
                break;
            case '%':
                this.final.push((scope) => operand1(scope) % operand2(scope));
                break;
        }
    }
    pushCase(node) {
        const conds = [];
        let elseThen = null;
        if (node.else != null) {
            elseThen = this.final.pop();
        }
        for (const whenNode of node.when) {
            const then = this.final.pop();
            const cond = this.final.pop();
            conds.unshift([cond, then]);
        }
        this.final.push((scope) => {
            for (let i = 0; i < conds.length; i++) {
                if (conds[i][0](scope)) {
                    return conds[i][1](scope);
                }
            }
            if (elseThen != null) {
                return elseThen(scope);
            }
            return null;
        });
    }
    pushField(node) {
        const startLoc = node.location.start;
        const field = this.fieldResolver.getField(startLoc.offset);
        if (field != null) {
            switch (field.type) {
                case FieldType.DATA: {
                    const columnName = field.column.name;
                    this.final.push((scope) => {
                        return scope.row[columnName];
                        // return scope.getRowValue(colName);
                    });
                    break;
                }
                case FieldType.MODELCALC: {
                    const func = this.funcResolver.getCalcFunc(field);
                    this.functions.push(func);
                    switch (func.type) {
                        case 'SCALAR':
                            this.pushScalarCalc(node, func);
                            break;
                        case 'AGG':
                            this.pushInitCalc(node, func);
                            this.pushStepCalc(node, func);
                            this.pushFinalCalc(node, func);
                            break;
                    }
                    break;
                }
            }
        }
        else {
            throw new Error(`(${startLoc.line}:${startLoc.column}): ERROR Unable to resolve field [${node.value}]`);
        }
    }
    pushCall(node) {
        const startLoc = node.location.start;
        const func = this.funcResolver.getFunc(startLoc.offset);
        if (func != null) {
            this.functions.push(func);
            const args = [];
            for (let i = 0; i < func.argc; i++) {
                args.unshift(this.final.pop());
            }
            switch (func.type) {
                case 'SCALAR':
                    this.pushScalarCall(node, func, args);
                    break;
                case 'AGG':
                    this.pushInitCall(node, func, args);
                    this.pushStepCall(node, func, args);
                    this.pushFinalCall(node, func, args);
                    break;
            }
        }
        else {
            throw new Error(`(${startLoc.line}:${startLoc.column}): ERROR Unable to resolve function [${node.callee}]`);
        }
    }
    /**
     * Init
     */
    pushInitCall(node, func, args) {
        this.init.push((scope) => {
            func.init(scope);
        });
    }
    pushInitCalc(node, func) {
        this.init.push((scope) => {
            func.init(scope);
        });
    }
    /**
     * Step
     */
    pushStepCall(node, func, args) {
        this.step.push((scope) => {
            func.step(scope, ...args);
        });
    }
    pushStepCalc(node, func) {
        this.step.push((scope) => {
            func.step(scope);
        });
    }
    /**
     * Final
     */
    pushScalarCall(node, func, args) {
        this.final.push((scope) => func.value(scope, ...args));
    }
    pushScalarCalc(node, func) {
        this.final.push((scope) => func.value(scope));
    }
    pushFinalCall(node, func, args) {
        this.final.push((scope) => func.final(scope));
    }
    pushFinalCalc(node, func) {
        this.final.push((scope) => func.final(scope));
    }
}
